import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import TabGaps from "./TabGaps"
import TabIncremental from "./TabIncremental"
import TabHistorial from "./TabHistorial"

const TABS = [
  { id: "gaps", label: "Folios faltantes", icon: "mdi-alert-circle-outline" },
  { id: "incremental", label: "Cambios", icon: "mdi-trending-up" },
  { id: "historial", label: "Historial", icon: "mdi-clock-outline" },
]

const DrawerDetalle = ({
  abierto,
  onCerrar,
  prefijo,
  nitemisor,
  empresaNombre,
  fechaDesde,
  fechaHasta,
  onDescargarCSV,
  exportingPrefijo,
}) => {
  const [tabActivo, setTabActivo] = useState("gaps")

  useEffect(() => {
    if (abierto) setTabActivo("gaps")
  }, [abierto, prefijo?.prefijo])

  useEffect(() => {
    if (!abierto) return
    const handler = e => {
      if (e.key === "Escape") onCerrar()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [abierto, onCerrar])

  if (!abierto || !prefijo) return null

  const gaps = prefijo.gaps_estimados ?? 0
  const exportando = exportingPrefijo === prefijo.prefijo

  return (
    <>
      <div
        onClick={onCerrar}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.5)",
          zIndex: 1040,
          animation: "fadeIn 0.2s ease",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(900px, 95vw)",
          background: "#f8fafc",
          zIndex: 1050,
          boxShadow: "-8px 0 32px rgba(15, 23, 42, 0.15)",
          display: "flex",
          flexDirection: "column",
          animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <style>{`
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            background: "linear-gradient(135deg, #0f172a, #1e3a5f)",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 8 }}>
              Prefijo
              {prefijo.es_compartido && (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(251, 191, 36, 0.2)",
                    color: "#fbbf24",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  <i className="mdi mdi-source-branch me-1" />
                  Compartido
                </span>
              )}
            </div>
            <h4 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#fff", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
              {prefijo.prefijo}
            </h4>
            {(prefijo.sucursales_lista || []).length > 0 && (
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 6 }}>
                <i className="mdi mdi-source-branch me-1" />
                {prefijo.sucursales_lista.join(" + ")}
              </div>
            )}
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
              {empresaNombre} · NIT {nitemisor}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => onDescargarCSV(prefijo)}
              disabled={gaps === 0 || exportando}
              title={gaps === 0 ? "Sin gaps para exportar" : "Descargar CSV completo"}
            >
              {exportando ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <i className="mdi mdi-file-download-outline me-1" />
                  CSV
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCerrar}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "none",
                color: "#fff",
                width: 36,
                height: 36,
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Cerrar (Esc)"
            >
              <i className="mdi mdi-close" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 24px" }}>
          <div style={{ display: "flex", gap: 4 }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTabActivo(tab.id)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "14px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    color: activo ? "#0f172a" : "#64748b",
                    borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                    cursor: "pointer",
                    transition: "color 0.15s",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {tabActivo === "gaps" && (
            <TabGaps
              nitemisor={nitemisor}
              prefijo={prefijo.prefijo}
              fechaDesde={fechaDesde}
              fechaHasta={fechaHasta}
            />
          )}
          {tabActivo === "incremental" && (
            <TabIncremental nitemisor={nitemisor} prefijo={prefijo.prefijo} />
          )}
          {tabActivo === "historial" && (
            <TabHistorial nitemisor={nitemisor} prefijo={prefijo.prefijo} />
          )}
        </div>
      </div>
    </>
  )
}

DrawerDetalle.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
  prefijo: PropTypes.object,
  nitemisor: PropTypes.string,
  empresaNombre: PropTypes.string,
  fechaDesde: PropTypes.string,
  fechaHasta: PropTypes.string,
  onDescargarCSV: PropTypes.func.isRequired,
  exportingPrefijo: PropTypes.string,
}

export default DrawerDetalle
